import { parseJariFile } from "@jari-tools/shared";
import debugSampleJari from "../fixtures/debug-sample.jari?raw";
import {
  FACTORS,
  TOP_SECTIONS,
  type DriehoekData,
  type Factor,
  type TopSection,
  type TopSectionItem,
  type TriangleItem,
} from "../types/driehoek-data";
import { clampToSector, randomPointInSector, sectorCentroid } from "./geometry";

export function getDefaultData(): DriehoekData {
  const topSections = {} as DriehoekData["topSections"];
  for (const section of TOP_SECTIONS) {
    topSections[section] = [];
  }
  return { name: "", basisgegevens: "", topSections, items: [] };
}

export function normalizeImportedData(raw: unknown): DriehoekData | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Record<string, unknown>;
  const payload =
    d.data && typeof d.data === "object" ? (d.data as Record<string, unknown>) : d;
  if (!payload.topSections && !Array.isArray(payload.items)) return null;

  const topSections = {} as DriehoekData["topSections"];
  for (const section of TOP_SECTIONS) {
    const arr = (payload.topSections as Record<string, unknown[]>)?.[section] || [];
    topSections[section] = arr.map((it) => {
      const item = it as TopSectionItem;
      return {
        id: item.id || crypto.randomUUID(),
        text: String(item.text || "").trim(),
      };
    });
  }

  const items = ((payload.items as TriangleItem[]) || []).map((it) => {
    const factor = (FACTORS.includes(it.factor as Factor) ? it.factor : "gezin") as Factor;
    const hasXY = typeof it.x === "number" && typeof it.y === "number";
    const [x, y] = hasXY ? [it.x, it.y] : sectorCentroid(factor);
    const [cx, cy] = clampToSector(x, y, factor);
    return {
      id: it.id || crypto.randomUUID(),
      text: String(it.text || ""),
      factor,
      x: cx,
      y: cy,
    };
  });

  return {
    name: String(payload.name || ""),
    basisgegevens: String(payload.basisgegevens || ""),
    topSections,
    items,
  };
}

/** Bundled test fixture for Ctrl+D debug mode. */
export function getDebugFixtureData(): DriehoekData | null {
  return parseDriehoekFile(debugSampleJari);
}

export function parseDriehoekFile(text: string): DriehoekData | null {
  try {
    const raw = parseJariFile<unknown>(text, "context-driehoek");
    return normalizeImportedData(raw);
  } catch {
    try {
      return normalizeImportedData(JSON.parse(text.replace(/^\uFEFF/, "")));
    } catch {
      return null;
    }
  }
}

export function addTopSectionItem(
  data: DriehoekData,
  section: TopSection,
  text: string
): DriehoekData {
  const item: TopSectionItem = { id: crypto.randomUUID(), text: text.trim() };
  return {
    ...data,
    topSections: {
      ...data.topSections,
      [section]: [...data.topSections[section], item],
    },
  };
}

export function updateTopSectionItem(
  data: DriehoekData,
  id: string,
  updates: Partial<TopSectionItem>
): DriehoekData {
  const newTopSections = { ...data.topSections };
  for (const section of TOP_SECTIONS) {
    const items = newTopSections[section];
    const idx = items.findIndex((i) => i.id === id);
    if (idx >= 0) {
      newTopSections[section] = items.map((it) =>
        it.id === id ? { ...it, ...updates } : it
      );
      break;
    }
  }
  return { ...data, topSections: newTopSections };
}

export function deleteTopSectionItem(data: DriehoekData, id: string): DriehoekData {
  const newTopSections = { ...data.topSections };
  for (const section of TOP_SECTIONS) {
    newTopSections[section] = newTopSections[section].filter((it) => it.id !== id);
  }
  return { ...data, topSections: newTopSections };
}

export function addItem(data: DriehoekData, factor: Factor, text: string): DriehoekData {
  const [x, y] = randomPointInSector(factor);
  return {
    ...data,
    items: [
      ...data.items,
      { id: crypto.randomUUID(), text: text.trim(), factor, x, y },
    ],
  };
}

export function updateItem(
  data: DriehoekData,
  id: string,
  updates: Partial<TriangleItem>
): DriehoekData {
  return {
    ...data,
    items: data.items.map((it) => (it.id === id ? { ...it, ...updates } : it)),
  };
}

export function deleteItem(data: DriehoekData, id: string): DriehoekData {
  return { ...data, items: data.items.filter((it) => it.id !== id) };
}

export function clearAllData(data: DriehoekData): DriehoekData {
  const topSections = {} as DriehoekData["topSections"];
  for (const s of TOP_SECTIONS) topSections[s] = [];
  return { ...data, basisgegevens: "", topSections, items: [] };
}
